/**
 * Form that mounts Stripe Payment Element and confirms SetupIntent.
 * Must be rendered inside <Elements> from @stripe/react-stripe-js.
 * Only enables submit after the Payment Element has mounted (onReady) to avoid
 * "elements should have a mounted Payment Element" from confirmSetup().
 */
import { Box, Stack, Typography } from '@mui/material';
import { PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import { openToast } from '../../store/reducers/toastSlice';
import { trackPaymentMethodAddFailed, trackPaymentMethodAdded } from '../../utils/google-analytics';
import { useTranslation } from '../../utils/translations';
import CustomButton from '../button/ButtonComponent';
import { colors } from '../theme/theme';

import type { SavedPaymentMethodDisplay } from './AddPaymentMethodModal';
import type { StripePaymentElementChangeEvent, StripeError } from '@stripe/stripe-js';

const PAYMENT_ELEMENT_READY_TIMEOUT_MS = 10000;

interface AddPaymentMethodFormProps {
    onSuccess: (paymentMethod: SavedPaymentMethodDisplay) => void;
    onCancel: () => void;
}

export function PaymentMethodForm({ onSuccess, onCancel }: AddPaymentMethodFormProps) {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const stripe = useStripe();
    const elements = useElements();
    const [error, setError] = useState<string | null>(null);
    const [isProcessing, setIsProcessing] = useState(false);
    const [paymentElementReady, setPaymentElementReady] = useState(false);
    const [linkAlreadyUsed, setLinkAlreadyUsed] = useState(false);

    useEffect(() => {
        console.log('[PaymentMethodForm] mounted. stripe ready:', !!stripe, 'elements ready:', !!elements);
    }, [stripe, elements]);

    // If Payment Element never becomes ready (e.g. SetupIntent already consumed), show message
    useEffect(() => {
        if (!paymentElementReady && !error && !linkAlreadyUsed) {
            const timer = setTimeout(() => {
                console.error(
                    `[PaymentMethodForm] PaymentElement onReady did not fire within ${PAYMENT_ELEMENT_READY_TIMEOUT_MS}ms — ` +
                        'look above for a Stripe.js IntegrationError/console error explaining why it failed to mount.',
                );
                setLinkAlreadyUsed(true);
                setError(t('addPaymentMethodForm.paymentFormLinkExpired'));
            }, PAYMENT_ELEMENT_READY_TIMEOUT_MS);
            return () => clearTimeout(timer);
        }
        return undefined;
    }, [paymentElementReady, linkAlreadyUsed, error, t, stripe, elements]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!stripe || !elements || !paymentElementReady) return;

        setError(null);
        setIsProcessing(true);

        try {
            console.log('[PaymentMethodForm] calling stripe.confirmSetup...');
            const { error: confirmError, setupIntent } = await stripe.confirmSetup({
                elements,
                confirmParams: {
                    return_url: `${window.location.origin}/dashboard`,
                },
                redirect: 'if_required',
            });

            if (confirmError) {
                console.error(
                    '[PaymentMethodForm] confirmSetup error:',
                    confirmError.type,
                    confirmError.code,
                    confirmError.message,
                );
                trackPaymentMethodAddFailed('card', confirmError.message);
                setError(confirmError.message ?? t('addPaymentMethodForm.somethingWentWrong'));
                setIsProcessing(false);
                return;
            }
            console.log('[PaymentMethodForm] confirmSetup succeeded, setupIntent status:', setupIntent?.status);

            if (setupIntent?.status === 'succeeded' && setupIntent.payment_method) {
                const pmId =
                    typeof setupIntent.payment_method === 'string'
                        ? setupIntent.payment_method
                        : ((setupIntent.payment_method as { id?: string })?.id ?? 'unknown');
                const pm = typeof setupIntent.payment_method === 'object' ? setupIntent.payment_method : null;
                const card = pm && 'card' in pm ? pm.card : null;

                // Check funding type: only accept credit cards
                const funding = (card as { funding?: string })?.funding;
                const brand = (card as { brand?: string })?.brand;
                console.log('[PaymentMethodForm] Card funding type:', funding, 'Brand:', brand, 'Card:', card);
                if (card && funding !== 'credit') {
                    // Reject debit, prepaid, and unknown cards - detach the card
                    console.log('[PaymentMethodForm] Rejecting non-credit card with funding:', funding);
                    trackPaymentMethodAddFailed('card', `non_credit_card:${funding ?? 'unknown'}`);
                    try {
                        await fetch(`/api/bank-accounts/stripe-card/${pmId}`, { method: 'DELETE' });
                    } catch (detachErr) {
                        console.error('Error detaching card:', detachErr);
                    }
                    // Show rejection message
                    dispatch(
                        openToast({
                            show: true,
                            isAlertOpen: true,
                            message: t('makePayment.cardNotEligibleOnlyCredit'),
                            severity: 'error',
                        }),
                    );
                    onCancel?.();
                    return;
                }

                const display: SavedPaymentMethodDisplay = {
                    id: pmId,
                    label: card
                        ? `${card.brand ?? 'Card'} •••• ${card.last4 ?? '****'}`
                        : t('addPaymentMethodForm.paymentMethodLabel'),
                    last4: card?.last4,
                    brand: card?.brand,
                    expiryMonth: card?.exp_month ?? undefined,
                    expiryYear: card?.exp_year ?? undefined,
                    funding: card?.funding,
                    country: card ? ((card as Record<string, unknown>).country as string | undefined) : undefined,
                };
                trackPaymentMethodAdded('card');
                onSuccess(display);
            } else {
                trackPaymentMethodAdded('card');
                onSuccess({
                    id: typeof setupIntent?.payment_method === 'string' ? setupIntent.payment_method : 'unknown',
                    label: t('addPaymentMethodForm.paymentMethodLabel'),
                });
            }
        } catch (err: unknown) {
            const message = err instanceof Error ? err.message : undefined;
            trackPaymentMethodAddFailed('card', message);
            setError(message ?? t('addPaymentMethodForm.failedToSavePaymentMethod'));
        } finally {
            setIsProcessing(false);
        }
    };

    return (
        <form onSubmit={handleSubmit}>
            <Stack spacing={2}>
                {!paymentElementReady && !linkAlreadyUsed && (
                    <Typography variant="body2" color="textSecondary" sx={{ fontStyle: 'italic' }}>
                        {t('addPaymentMethodModal.loadingPaymentForm')}
                    </Typography>
                )}
                <Box
                    sx={{
                        minHeight: 120,
                        '& .InputElement': {
                            padding: 1.5,
                            border: `1px solid ${colors.greyPreset?.[30] || '#ddd'}`,
                            borderRadius: 1,
                        },
                    }}
                >
                    <PaymentElement
                        options={{
                            layout: 'tabs',
                            paymentMethodOrder: ['card', 'us_bank_account', 'upi', 'link'],
                            defaultCollapsed: false,
                        }}
                        onReady={() => {
                            console.log('[PaymentMethodForm] PaymentElement onReady fired');
                            setPaymentElementReady(true);
                            setError(null);
                            setLinkAlreadyUsed(false);
                        }}
                        onLoadError={(event: { elementType: 'payment'; error: StripeError }) => {
                            console.error('[PaymentMethodForm] PaymentElement onLoadError:', event.error);
                            setPaymentElementReady(false);
                            setLinkAlreadyUsed(true);
                            setError(event.error?.message ?? t('addPaymentMethodForm.paymentFormLinkExpired'));
                        }}
                        onChange={(event: StripePaymentElementChangeEvent) => {
                            if (event.value?.type) {
                                console.log(
                                    '[PaymentMethodForm] PaymentElement onChange, selected type:',
                                    event.value.type,
                                );
                            }
                        }}
                    />
                </Box>
                {error && (
                    <Typography variant="body2" role="alert" sx={{ color: colors.errorPreset?.[10] || '#d32f2f' }}>
                        {error}
                    </Typography>
                )}
                <Stack direction="row" spacing={1.5} justifyContent="flex-end" sx={{ pt: 1 }}>
                    <CustomButton type="button" variant="outlined" onClick={onCancel} disabled={isProcessing}>
                        {t('common.Cancel')}
                    </CustomButton>
                    <CustomButton
                        type="submit"
                        variant="contained"
                        disabled={!stripe || !elements || !paymentElementReady || isProcessing || linkAlreadyUsed}
                    >
                        {isProcessing ? t('addPaymentMethodForm.saving') : t('addPaymentMethodForm.savePaymentMethod')}
                    </CustomButton>
                </Stack>
            </Stack>
        </form>
    );
}

export default PaymentMethodForm;
